import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { Shield, Save, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const ROLES = ["ADMIN", "DEPARTMENT_HEAD", "MANAGER", "HR", "AUDIT", "EMPLOYEE"];
const MODULES = ["tickets", "tasks", "crm", "quality", "communications", "projects", "approvals", "reports", "users", "whmcs-tickets", "requests", "leave-balances", "hr"];
const MODULE_LABELS: Record<string, string> = {
  "whmcs-tickets": "WHMCS Tickets & Quality",
  "requests": "Leave & Overtime Requests",
  "leave-balances": "Leave Balances",
  "hr": "HR — Employee Data",
};
const PERMS = ["canView", "canCreate", "canEdit", "canDelete", "ownOnly"] as const;
const PERM_LABELS: Record<string, string> = {
  canView: "View", canCreate: "Create", canEdit: "Edit", canDelete: "Delete", ownOnly: "Own Only"
};
const ROLE_COLORS: Record<string, string> = {
  ADMIN: "bg-red-100 text-red-700 border-red-200",
  DEPARTMENT_HEAD: "bg-purple-100 text-purple-700 border-purple-200",
  MANAGER: "bg-blue-100 text-blue-700 border-blue-200",
  HR: "bg-indigo-100 text-indigo-700 border-indigo-200",
  AUDIT: "bg-teal-100 text-teal-700 border-teal-200",
  EMPLOYEE: "bg-gray-100 text-gray-700 border-gray-200",
};

type PermMap = Record<string, Record<string, any>>;

const RolesPermissions: React.FC = () => {
  const { refetchPermissions } = useAuth();
  const [perms, setPerms] = useState<PermMap>({});
  const [activeRole, setActiveRole] = useState("EMPLOYEE");
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/permissions");
      const map: PermMap = {};
      for (const p of res.data) {
        if (!map[p.role]) map[p.role] = {};
        map[p.role][p.module] = p;
      }
      setPerms(map);
    } catch { toast.error("Failed to load permissions"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (role: string, module: string, perm: string) => {
    setPerms(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: { ...(prev[role]?.[module] || {}), [perm]: !prev[role]?.[module]?.[perm] }
      }
    }));
  };

  const save = async (module: string) => {
    setSaving(module);
    try {
      const data = perms[activeRole]?.[module] || {};
      await api.put(`/permissions/${activeRole}/${module}`, {
        canView: !!data.canView, canCreate: !!data.canCreate,
        canEdit: !!data.canEdit, canDelete: !!data.canDelete, ownOnly: !!data.ownOnly,
      });
      toast.success(`Saved ${module} permissions`);
      refetchPermissions();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(null); }
  };

  const seedDefaults = async () => {
    try { await api.post("/permissions/seed"); toast.success("Default permissions loaded"); load(); refetchPermissions(); }
    catch { toast.error("Failed"); }
  };

  const roleData = perms[activeRole] || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Shield size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Roles & Permissions</h2>
            <p className="text-sm text-gray-500">Control what each role can access</p>
          </div>
        </div>
        <button onClick={seedDefaults} className="flex items-center gap-2 border border-gray-300 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">
          <RefreshCw size={14} /> Reset Defaults
        </button>
      </div>

      {/* Role Tabs */}
      <div className="flex gap-2 flex-wrap">
        {ROLES.map(role => (
          <button key={role} onClick={() => setActiveRole(role)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
              activeRole === role ? ROLE_COLORS[role] + " shadow-sm" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}>
            {role.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Role description */}
      <div className={`rounded-xl border p-4 text-sm ${ROLE_COLORS[activeRole]}`}>
        {activeRole === "ADMIN" && "Full system access — can view, create, edit, and delete everything."}
        {activeRole === "DEPARTMENT_HEAD" && "Can view and manage all data within their department across all managers and employees."}
        {activeRole === "MANAGER" && "Can view and edit data created by their assigned team members."}
        {activeRole === "HR" && "HR role — manages employee HR data (personal, contact, employment, insurance, skills)."}
        {activeRole === "AUDIT" && "Audit role — view-only access to all modules, with create and edit access on Quality."}
        {activeRole === "EMPLOYEE" && "Limited access — can only view and manage their own records."}
      </div>

      {/* Permissions Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading permissions...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-5 py-3 text-left font-semibold text-gray-700">Module</th>
                {PERMS.map(p => (
                  <th key={p} className="px-4 py-3 text-center font-semibold text-gray-700">{PERM_LABELS[p]}</th>
                ))}
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Save</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {MODULES.map(mod => {
                const modPerms = roleData[mod] || {};
                const isAdmin = activeRole === "ADMIN";
                return (
                  <tr key={mod} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-800 capitalize">{MODULE_LABELS[mod] || mod}</td>
                    {PERMS.map(perm => (
                      <td key={perm} className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isAdmin ? true : !!modPerms[perm]}
                          disabled={isAdmin}
                          onChange={() => !isAdmin && toggle(activeRole, mod, perm)}
                          className="w-4 h-4 text-blue-600 rounded cursor-pointer disabled:cursor-default"
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center">
                      {!isAdmin && (
                        <button onClick={() => save(mod)} disabled={saving === mod}
                          className="flex items-center gap-1 mx-auto bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 text-xs disabled:opacity-60">
                          <Save size={12} /> {saving === mod ? "..." : "Save"}
                        </button>
                      )}
                      {isAdmin && <span className="text-xs text-gray-400">Full Access</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
export default RolesPermissions;
